/**
 * v1.14.60 — Quoter B2B Admin (Milestone 1: lista prodotti + detail base)
 *
 * Pagina admin per gestire prodotti Quoter multi-prodotto.
 * MVP: lista prodotti con info chiave + drill-down detail con tariff/CGA/template.
 *
 * Milestone successive: editor tariff JSON, upload Excel, calcolo premi, PDF gen.
 */
import React, { useEffect, useState } from "react";
import QuoterInvitesPanel from "./QuoterInvitesPanel";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, FileText, Download, RefreshCw, Eye, Layers, ChevronLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axc = { withCredentials: true };

function ProductCard({ p, onOpen }) {
  return (
    <div className="border border-zinc-200 rounded-xl p-4 hover:border-emerald-400 hover:shadow-md transition cursor-pointer bg-white" onClick={() => onOpen(p.slug)} data-testid={`product-card-${p.slug}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-emerald-700">{p.compagnia}</div>
          <h3 className="font-bold text-lg leading-tight">{p.nome}</h3>
        </div>
        <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${p.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-600"}`}>
          {p.status}
        </span>
      </div>
      <p className="text-xs text-zinc-600 mb-3 line-clamp-2">{p.descrizione}</p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-zinc-50 rounded p-2">
          <div className="text-[10px] uppercase text-zinc-500">Tariffa</div>
          <div className="font-semibold truncate">{p.current_tariff?.version_label || "—"}</div>
        </div>
        <div className="bg-zinc-50 rounded p-2">
          <div className="text-[10px] uppercase text-zinc-500">CGA</div>
          <div className="font-semibold truncate">{p.current_cga?.version_label || "—"}</div>
          {p.current_cga?.size_bytes && (
            <div className="text-[10px] text-zinc-400">{Math.round(p.current_cga.size_bytes / 1024)} KB</div>
          )}
        </div>
      </div>
      <div className="mt-3 flex justify-between items-center text-xs text-zinc-500">
        <span>📊 {p.quote_count} quotazioni</span>
        <span className="flex flex-wrap gap-1">
          {(p.sezioni || []).slice(0, 3).map((s) => (
            <span key={s} className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-mono">{s}</span>
          ))}
        </span>
      </div>
    </div>
  );
}

function ProductDetail({ slug, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extractJob, setExtractJob] = useState(null);
  const [pollTimer, setPollTimer] = useState(null);

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/admin/quoter/products/${slug}`, axc)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(`Errore: ${e.response?.data?.detail || e.message}`))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    return () => { if (pollTimer) clearInterval(pollTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const launchAIExtraction = async () => {
    if (!window.confirm("Avvio estrazione AI della tariffa dal PDF Reale Mutua?\n\nDurata stimata: 60-180 secondi.\nCosto stimato: ~$0.30 (GPT-5 / Gemini Flash).")) return;
    try {
      const { data: job } = await axios.post(`${API}/admin/quoter/products/${slug}/tariff/extract-from-pdf`, {}, axc);
      setExtractJob({ ...job, polling: true });
      toast.info(`Job ${job.job_id.slice(0, 8)}… avviato`);
      // Polling ogni 8s
      const timer = setInterval(async () => {
        try {
          const { data: status } = await axios.get(`${API}/admin/quoter/tariff-extraction-jobs/${job.job_id}`, axc);
          setExtractJob({ ...status, polling: status.status === "running" });
          if (status.status !== "running") {
            clearInterval(timer);
            setPollTimer(null);
            if (status.status === "completed") {
              toast.success(`Estrazione OK · ${status.total_activities} codici attività estratti`);
              reload();
            } else {
              toast.error(`Estrazione fallita: ${(status.error || '').slice(0, 100)}`);
            }
          }
        } catch (_e) { /* swallow */ }
      }, 8000);
      setPollTimer(timer);
    } catch (e) {
      toast.error(`Errore avvio: ${e.response?.data?.detail || e.message}`);
    }
  };

  const activateTariffVersion = async (tariffId) => {
    if (!window.confirm("Attivare questa versione tariffaria?\n\nLa versione attualmente attiva verrà archiviata.")) return;
    try {
      await axios.post(`${API}/admin/quoter/tariff-versions/${tariffId}/activate`, {}, axc);
      toast.success("Versione tariffa attivata");
      reload();
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
    }
  };

  if (loading) return <div className="p-6 text-center text-sm text-zinc-500">Caricamento…</div>;
  if (!data) return <div className="p-6 text-sm text-red-500">Prodotto non trovato</div>;

  const { product, current_tariff, tariff_history, cga_history, excel_template, recent_quotations } = data;
  const dlPdf = (kind) => {
    window.open(`${API}/admin/quoter/products/${slug}/${kind}-pdf`, "_blank");
  };

  return (
    <div className="space-y-4" data-testid={`product-detail-${slug}`}>
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn">
        <ChevronLeft className="w-4 h-4" /> Torna alla lista
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{product.nome}</CardTitle>
          <div className="text-xs text-zinc-500">{product.compagnia} · slug: <code>{product.slug}</code></div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-700 mb-4">{product.descrizione}</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-emerald-50 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-emerald-700">Tariffa corrente</div>
              <div className="text-sm font-semibold mt-1">{current_tariff?.version_label}</div>
              {current_tariff?.extraction_stats?.total_activities && (
                <div className="text-[10px] text-emerald-700 mt-1">🤖 {current_tariff.extraction_stats.total_activities} codici AI</div>
              )}
              <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => dlPdf("tariff")} data-testid="dl-tariff">
                <Download className="w-3 h-3" /> Scarica PDF
              </Button>
              <Button size="sm" variant="default" className="mt-1 w-full bg-emerald-700 hover:bg-emerald-800" onClick={launchAIExtraction} data-testid="extract-ai-btn" disabled={extractJob?.polling}>
                {extractJob?.polling ? "Estrazione in corso…" : "🤖 Estrai con AI"}
              </Button>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-indigo-700">CGA corrente</div>
              <div className="text-sm font-semibold mt-1">{cga_history?.[0]?.version_label}</div>
              <div className="text-[10px] text-zinc-500">{Math.round((cga_history?.[0]?.size_bytes || 0) / 1024)} KB · SHA256: {cga_history?.[0]?.file_hash_sha256?.slice(0, 12)}…</div>
              <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => dlPdf("cga")} data-testid="dl-cga">
                <Download className="w-3 h-3" /> Scarica PDF
              </Button>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-amber-700">Template Excel</div>
              <div className="text-sm font-semibold mt-1">{excel_template?.version_label} · {(excel_template?.columns || []).length} colonne</div>
              <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => window.open(`${API}/admin/quoter/products/${slug}/excel-template/download`, "_blank")}>
                <Download className="w-3 h-3" /> Scarica template
              </Button>
            </div>
          </div>
          {extractJob && (
            <div className="mt-4 bg-zinc-50 border rounded-lg p-3 text-xs" data-testid="extract-job-status">
              <div className="font-bold">Job {extractJob.job_id?.slice(0, 8)}… · status: <span className={extractJob.status === "completed" ? "text-emerald-700" : extractJob.status === "failed" ? "text-red-600" : "text-amber-600"}>{extractJob.status}</span></div>
              {extractJob.total_activities && <div className="mt-1">✅ {extractJob.total_activities} codici attività estratti</div>}
              {extractJob.error && <div className="mt-1 text-red-600">❌ {extractJob.error}</div>}
              {extractJob.polling && <div className="mt-1 text-zinc-500 italic">Estrazione in corso (può richiedere fino a 3 min)…</div>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tariff Versions ({tariff_history?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {(tariff_history || []).map((tv) => (
              <div key={tv.id} className="flex justify-between items-center border-b py-2 text-xs">
                <span>
                  <strong>{tv.version_label}</strong>
                  <span className={`ml-2 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${tv.status === "active" ? "bg-emerald-100 text-emerald-800" : tv.status === "draft" ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-600"}`}>
                    {tv.status}
                  </span>
                  {tv.extraction_method && <span className="ml-2 text-zinc-500">via {tv.extraction_method}</span>}
                  {tv.extraction_stats?.total_activities && <span className="ml-2 text-emerald-700">· {tv.extraction_stats.total_activities} codici</span>}
                </span>
                {tv.status === "draft" && (
                  <Button size="sm" variant="default" className="h-7 px-3 bg-emerald-700" onClick={() => activateTariffVersion(tv.id)} data-testid={`activate-tariff-${tv.id}`}>
                    Attiva
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Tariffa Corrente — Struttura JSON</CardTitle></CardHeader>
        <CardContent>
          <div className="bg-zinc-900 text-emerald-200 text-xs p-3 rounded overflow-x-auto max-h-96">
            <pre>{JSON.stringify(current_tariff?.rates, null, 2)}</pre>
          </div>
          {current_tariff?.extraction_method === "ai_standard" || current_tariff?.extraction_method === "ai_premium" || current_tariff?.extraction_method === "ai_fast" ? (
            <div className="text-xs text-emerald-700 mt-2">
              ✨ Estratto automaticamente da AI · {current_tariff?.extraction_stats?.total_activities} codici attività · metodo {current_tariff?.extraction_method}
            </div>
          ) : (
            <div className="text-xs text-zinc-500 mt-2 italic">
              Bozza placeholder. Clicca "🤖 Estrai con AI" per popolare automaticamente dal PDF tariffario.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Template Excel — Colonne ({(excel_template?.columns || []).length})</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-2 text-xs">
            {(excel_template?.columns || []).map((c) => (
              <div key={c.name} className="bg-zinc-50 rounded p-2 border-l-2 border-amber-400">
                <div className="font-bold">{c.label}</div>
                <div className="text-zinc-500 font-mono">{c.name} <span className="text-amber-700">[{c.type}]</span>{c.required && <span className="text-red-500 ml-1">*</span>}</div>
                {c.example && <div className="text-zinc-400">es: <code>{String(c.example)}</code></div>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Quotazioni recenti ({recent_quotations?.length || 0})</CardTitle></CardHeader>
        <CardContent>
          {!recent_quotations?.length ? (
            <div className="text-sm text-zinc-500 italic">Nessuna quotazione ancora generata. Disponibile da Milestone 2 (upload Excel + calcolo premi).</div>
          ) : (
            <div className="space-y-1">
              {recent_quotations.map((q) => (
                <div key={q.id || q.preventivo_numero} className="text-xs flex justify-between border-b py-1">
                  <span>{q.preventivo_numero} · {q.cliente_nome}</span>
                  <span className="font-mono text-emerald-700">€{q.totale_premio_eur}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminQuoterPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [view, setView] = useState("products"); // products | new-quotation | quotation-result

  // Wizard state
  const [wProduct, setWProduct] = useState("composita_reale");
  const [wCliente, setWCliente] = useState({ nome: "", piva: "", email: "" });
  const [wFile, setWFile] = useState(null);
  const [wLoading, setWLoading] = useState(false);
  const [wResult, setWResult] = useState(null);
  const [quotations, setQuotations] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quoter/products`, axc);
      setProducts(data.items || []);
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadQuotations = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/quoter/quotations`, axc);
      setQuotations(data.items || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); loadQuotations(); }, []);

  const downloadTemplate = () => {
    window.open(`${API}/admin/quoter/products/${wProduct}/excel-template/download`, "_blank");
  };

  const submitQuotation = async () => {
    if (!wFile) return toast.error("Carica un file Excel");
    if (!wCliente.nome) return toast.error("Inserisci nome cliente");
    setWLoading(true); setWResult(null);
    try {
      const fd = new FormData();
      fd.append("file", wFile);
      fd.append("product_slug", wProduct);
      fd.append("cliente_nome", wCliente.nome);
      fd.append("cliente_piva", wCliente.piva);
      fd.append("cliente_email", wCliente.email);
      const { data } = await axios.post(`${API}/admin/quoter/quotations`, fd, {
        ...axc, headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      setWResult(data);
      toast.success(`Quotazione ${data.preventivo_numero} creata · totale €${data.totale_premio_eur}`);
      loadQuotations();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || e.message));
    } finally {
      setWLoading(false);
    }
  };

  if (selectedSlug) {
    return <ProductDetail slug={selectedSlug} onBack={() => setSelectedSlug(null)} />;
  }

  return (
    <div className="space-y-4" data-testid="admin-quoter-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-6 h-6 text-emerald-600" /> Quoter B2B
            <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full uppercase font-bold">v3 AUTO</span>
          </h1>
          <p className="text-sm text-zinc-500">Multi-rischio · upload Excel · calcolo premi automatico</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={view === "products" ? "default" : "outline"} onClick={() => setView("products")} data-testid="view-products">Prodotti</Button>
          <Button size="sm" variant={view === "new-quotation" ? "default" : "outline"} onClick={() => setView("new-quotation")} data-testid="view-new-quotation">+ Nuova quotazione</Button>
          <Button size="sm" variant={view === "quotation-list" ? "default" : "outline"} onClick={() => { setView("quotation-list"); loadQuotations(); }} data-testid="view-quotation-list">Storico ({quotations.length})</Button>
          <Button size="sm" variant={view === "invites" ? "default" : "outline"} onClick={() => setView("invites")} data-testid="view-invites">Inviti Cliente</Button>
        </div>
      </div>

      {view === "invites" && (
        <QuoterInvitesPanel products={products} />
      )}

      {view === "products" && (
        <>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
            <strong>🤖 v3 Auto-Resolve attivo</strong> · 15 colonne Excel (Classe Territoriale e Codice Attività dedotti automaticamente dal sistema). Tariffa AI-extracted: 79+ codici Reale Mutua. Limite 250 ubicazioni.
          </div>
          {loading ? <div className="text-center py-10 text-sm text-zinc-500">Caricamento…</div>
            : products.length === 0 ? <div className="text-center py-10 text-sm text-zinc-500 italic">Nessun prodotto</div>
            : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {products.map((p) => <ProductCard key={p.slug} p={p} onOpen={setSelectedSlug} />)}
              </div>}
        </>
      )}

      {view === "new-quotation" && (
        <Card data-testid="new-quotation-wizard">
          <CardHeader><CardTitle>Nuova Quotazione — Wizard</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase text-zinc-600">Prodotto</label>
                <select value={wProduct} onChange={(e) => setWProduct(e.target.value)} className="w-full border rounded p-2 text-sm" data-testid="w-product">
                  {products.map(p => <option key={p.slug} value={p.slug}>{p.nome}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-zinc-600">1. Scarica Template Excel</label>
                <Button size="sm" variant="outline" onClick={downloadTemplate} className="w-full" data-testid="dl-template-btn">
                  <Download className="w-3 h-3" /> Scarica template ({wProduct})
                </Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <input placeholder="Nome cliente / Ragione sociale *" value={wCliente.nome} onChange={(e) => setWCliente({ ...wCliente, nome: e.target.value })} className="border rounded p-2 text-sm" data-testid="w-cliente-nome" />
              <input placeholder="Partita IVA" value={wCliente.piva} onChange={(e) => setWCliente({ ...wCliente, piva: e.target.value })} className="border rounded p-2 text-sm" data-testid="w-cliente-piva" />
              <input placeholder="Email" value={wCliente.email} onChange={(e) => setWCliente({ ...wCliente, email: e.target.value })} className="border rounded p-2 text-sm" data-testid="w-cliente-email" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-600">2. Carica Excel compilato (max 250 ubicazioni)</label>
              <input type="file" accept=".xlsx" onChange={(e) => setWFile(e.target.files[0])} className="block w-full text-sm" data-testid="w-file" />
              {wFile && <div className="text-xs text-zinc-500 mt-1">📎 {wFile.name} ({Math.round(wFile.size / 1024)} KB)</div>}
            </div>
            <Button onClick={submitQuotation} disabled={wLoading || !wFile} className="w-full" data-testid="submit-quotation">
              {wLoading ? "Calcolo in corso…" : "Calcola Premi"}
            </Button>
            {wResult && (
              <div className="bg-zinc-50 rounded-lg p-3 border" data-testid="quotation-result">
                <div className="flex justify-between items-center mb-2">
                  <div className="font-bold">{wResult.preventivo_numero}</div>
                  <div className="text-2xl font-bold text-emerald-700">€ {wResult.totale_premio_eur?.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="text-xs text-zinc-600 mb-2">{wResult.ubicazioni_count} ubicazioni · status: <span className="font-mono">{wResult.status}</span></div>
                {wResult.errors_per_row?.length > 0 && (
                  <details className="text-xs mb-2"><summary className="cursor-pointer text-amber-700 font-bold">{wResult.errors_per_row.length} warning di validazione</summary>
                    <pre className="bg-amber-50 p-2 rounded mt-1 text-[10px] overflow-x-auto">{JSON.stringify(wResult.errors_per_row, null, 2)}</pre>
                  </details>
                )}
                <div className="space-y-1 text-xs">
                  {(wResult.preview_first_5 || []).map((u, i) => {
                    const auto = u._auto_resolved || {};
                    const meta = u._resolution_meta || {};
                    const needsReview = meta.attivita_codice?.needs_review;
                    return (
                      <div key={i} className={`border-b py-1.5 ${needsReview ? "bg-amber-50" : ""}`} data-testid={`prev-row-${i}`}>
                        <div>
                          <span className="font-mono font-bold">{u.ubicazione_id}</span> · {u.comune} ({u.provincia})
                          <span className="ml-2 inline-flex items-center gap-1">
                            cl.<strong>{u.classe_territoriale}</strong>
                            {auto.classe_territoriale && (
                              <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded font-bold" title={meta.classe_territoriale?.rationale}>🤖 AUTO</span>
                            )}
                          </span>
                          <span className="ml-2 inline-flex items-center gap-1">
                            <code className="text-[10px]">{u.attivita_codice || "—"}</code>
                            {auto.attivita_codice && (
                              <span
                                className={`text-[9px] px-1 rounded font-bold ${needsReview ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}
                                title={`${meta.attivita_codice?.method} · conf ${meta.attivita_codice?.confidence} · ${meta.attivita_codice?.label_canonical || ""}`}
                              >
                                🤖 {meta.attivita_codice?.method?.toUpperCase()} {Math.round((meta.attivita_codice?.confidence || 0) * 100)}%
                              </span>
                            )}
                            {needsReview && (
                              <span className="text-[9px] bg-red-100 text-red-700 px-1 rounded font-bold">⚠ REVIEW</span>
                            )}
                          </span>
                          <span className="float-right">→ <strong className="text-emerald-700">€{u.totale_eur}</strong></span>
                        </div>
                        {u._settore_overridden_from && (
                          <div className="text-[10px] text-zinc-500 mt-0.5">
                            settore override: <s>{u._settore_overridden_from}</s> → <strong>{u.settore}</strong>
                          </div>
                        )}
                        {(meta.attivita_codice?.label_canonical || meta.classe_territoriale?.rationale) && (
                          <div className="text-[10px] text-zinc-500 italic mt-0.5">
                            {meta.attivita_codice?.label_canonical}
                            {meta.classe_territoriale?.rationale && <span className="ml-2">· {meta.classe_territoriale.rationale.slice(0, 70)}</span>}
                          </div>
                        )}
                        {u.warnings?.length > 0 && (
                          <div className="text-[10px] text-amber-700 mt-0.5">⚠ {u.warnings.join(" · ")}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="default" className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={() => window.open(`${API}/admin/quoter/quotations/${wResult.id}/pdf`, "_blank")} data-testid="dl-pdf-btn">
                    <FileText className="w-3 h-3" /> Scarica PDF Preventivo + CGA
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(`${API}/admin/quoter/quotations/${wResult.id}/pdf?regenerate=true`, "_blank")} data-testid="regen-pdf-btn" title="Forza rigenerazione (ignora cache)">
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {view === "quotation-list" && (
        <Card data-testid="quotation-list">
          <CardHeader><CardTitle>Storico Quotazioni</CardTitle></CardHeader>
          <CardContent>
            {quotations.length === 0 ? <div className="text-sm text-zinc-500 italic">Nessuna quotazione</div>
              : <div className="space-y-1">
                  {quotations.map(q => (
                    <div key={q.id} className="flex justify-between items-center border-b py-2 text-xs">
                      <span>
                        <strong>{q.preventivo_numero}</strong> · {q.cliente_nome}
                        <span className="text-zinc-500 ml-2">{q.created_at?.slice(0, 16)?.replace("T", " ")}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-emerald-700">€{q.totale_premio_eur?.toLocaleString("it-IT")}</span>
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => window.open(`${API}/admin/quoter/quotations/${q.id}/pdf`, "_blank")} data-testid={`dl-pdf-${q.id}`} title="Scarica PDF preventivo + CGA">
                          <FileText className="w-3 h-3" /> PDF
                        </Button>
                      </span>
                    </div>
                  ))}
                </div>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
